/**
 * Integrytas Group — locale-aware date formatting (CLAUDE.md §11).
 *
 * Every displayed date follows the active CSM locale's own convention —
 * EN_GB reads day/month/year, EN_US reads month/day/year — and always shows
 * all three (day, month, year) unless a spec explicitly asks for less.
 * Never render a raw ISO string (`row.created_at`) or a bare, unlocalized
 * `.toLocaleDateString()` directly in a page — use these instead.
 */

import { useCallback } from 'react';
import { csmLocaleToBcp47 } from '../i18n';
import { useActiveLocale } from './use_active_locale';

/** `YYYY-MM-DD` exactly — a Postgres `date` column, with no time and no zone. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  // A date-only string is a calendar date, not an instant: ECMAScript parses it
  // as UTC midnight, and Intl then renders that instant in the viewer's zone —
  // so west of UTC every `date` column displayed one day early (an order dated
  // 2026-08-09 read 08/08/2026 in EDT). Build it as LOCAL midnight instead, so
  // the day, month and year shown are the ones stored. Timestamps still carry a
  // real instant and are still converted to local time, which is correct.
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    const local = new Date(y, m - 1, d);
    return Number.isNaN(local.getTime()) ? null : local;
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 'day/month/year' order per the active locale (CSM code, e.g. 'EN_GB'). */
export function formatDate(
  value: string | number | Date | null | undefined,
  csmLocale: string | null | undefined,
): string {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(csmLocaleToBcp47(csmLocale), {
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

/** Same day/month/year convention, plus a locale-formatted time-of-day. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  csmLocale: string | null | undefined,
): string {
  const d = toDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat(csmLocaleToBcp47(csmLocale), {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(d);
}

/** Hook form — bound to the CURRENT active locale, so pages don't have to
 *  thread `useActiveLocale()` through themselves just to format a date. */
export function useFormatDate(): (value: string | number | Date | null | undefined) => string {
  const locale = useActiveLocale();
  return useCallback((value) => formatDate(value, locale), [locale]);
}

/** Hook form for date+time. */
export function useFormatDateTime(): (value: string | number | Date | null | undefined) => string {
  const locale = useActiveLocale();
  return useCallback((value) => formatDateTime(value, locale), [locale]);
}
